import DownloadIcon from '@mui/icons-material/Download'
import React, { ReactNode, useCallback, useState } from 'react'
import { Button, useNotify, useRefresh } from 'react-admin'
import { getUrl, httpClient } from '../../providers/data'

interface CsrDownloadButtonProps {
  certificateId: string
  // Replace the current CSR/certificate by a new key ("Vernieuw certificaat").
  renew?: boolean
  renderAs?: (onClick: () => void, loading: boolean) => ReactNode
}

const base64ToBlob = (b64: string, mime: string): Blob => {
  const byteChars = atob(b64)
  const bytes = new Uint8Array(byteChars.length)
  // eslint-disable-next-line security/detect-object-injection
  for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i)
  return new Blob([bytes], { type: mime })
}

const CsrDownloadButton: React.FC<CsrDownloadButtonProps> = ({ certificateId, renew, renderAs }) => {
  const notify = useNotify()
  const refresh = useRefresh()
  const [loading, setLoading] = useState(false)

  const onClick = useCallback(() => {
    setLoading(true)
    // Renew is a POST (new key and CSR); the GET then fetches the stored CSR.
    const renewed = renew
      ? httpClient(`${getUrl()}/certificate/${certificateId}/renew`, { method: 'POST' })
      : Promise.resolve()
    renewed
      .then(() => httpClient(`${getUrl()}/certificate/${certificateId}/csr`, { method: 'GET' }))
      .then(({ json }: { json: { id: string; filename: string; mimeType: string; data: string } }) => {
        const blob = base64ToBlob(json.data , (json.mimeType ) || 'application/x-pem-file')
        const url = URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = (json.filename ) || `certificate-${json.id}.csr.pem`
        document.body.appendChild(a)
        a.click()
        a.remove()
        URL.revokeObjectURL(url)
        notify('resources.certificates.actions.csrDownloaded', { type: 'info' })
        // A renew stays disabled until the refetched status replaces this button: a second
        // click would create yet another key and orphan the CSR just downloaded.
        if (!renew) setLoading(false)
      })
      .catch((e: unknown) => {
        console.error(e)
        // Show the server's (translated) reason, e.g. "Error renewing certificate.".
        notify((e as Error)?.message || 'ra.notification.http_error', { type: 'warning' })
        setLoading(false)
      })
      // Also on error: a renew POST may have succeeded before the GET failed.
      .finally(() => refresh())
  }, [certificateId, notify, renew])

  if (renderAs) return <>{renderAs(onClick, loading)}</>

  return (
    <Button
      label="resources.certificates.actions.downloadCsr"
      variant="outlined"
      startIcon={<DownloadIcon />}
      onClick={onClick}
      disabled={loading}
    />
  )
}

export default CsrDownloadButton
